import { observer } from 'mobx-react-lite'
import type { ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ListSettingsButton } from 'app/components/lists/ListSettingsButton'
import { ListViewToggle } from 'app/components/lists/ListViewToggle'
import { useRememberedListView } from 'app/components/lists/useRememberedListView'
import { ManualListBoard } from 'app/components/lists/manual/ManualListBoard'
import { ManualListTable } from 'app/components/lists/manual/ManualListTable'
import { useOpenListSettings } from 'app/components/lists/useOpenListSettings'
import type { List } from 'app/generated/graphql'
import type { ListView } from 'app/types/navigation'
import { useHandleSearchParams } from 'app/utils'

// A manual list offers exactly two views, never the system lists' `focus`.
const VIEWS: ReadonlyArray<ListView> = [`board`, `table`]

export const ManualListView = observer(function ManualListView({
  list,
  onStagesChanged,
}: {
  list: List
  onStagesChanged: () => void
}): ReactElement {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const [savedView, saveView, settingLoaded] = useRememberedListView(list.id, VIEWS)
  const openSettings = useOpenListSettings({ source: `practice`, list }, onStagesChanged)

  // Without a `?view=` to render immediately, wait for the saved view to load so the
  // page never flashes the board before swapping to a remembered table.
  if (!searchParams.has(`view`) && !settingLoaded) return <></>

  const urlView = searchParams.get(`view`)
  const view: ListView = VIEWS.includes(urlView as ListView)
    ? (urlView as ListView)
    : VIEWS.includes(savedView as ListView)
      ? (savedView as ListView)
      : `board`

  const viewToggle = (
    <ListViewToggle
      views={VIEWS}
      current={view}
      onSelect={(next) => {
        saveView(next)
        if (next !== view) handleSearchParams({ view: next })
      }}
    />
  )
  const settings = <ListSettingsButton onClick={openSettings} />

  // Keyed per list: the table's own key is its tab, which is `all` for every manual list, so
  // without this a row selection made in one list would still be live in the next.
  if (view === `table`)
    return <ManualListTable key={list.id} list={list} viewToggle={viewToggle} titleActions={settings} />
  return (
    <ManualListBoard list={list} viewToggle={viewToggle} titleActions={settings} onStagesChanged={onStagesChanged} />
  )
})
